import {
    Component,
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    OnInit,
    OnDestroy,
    ViewChild,
    ElementRef,
    inject
} from '@angular/core';
import { ConnectLogicService, ConnectState } from '@services/ymodem/connect-logic.service';
import { SerialService, SerialDeviceInfo, SZ_CODES } from '@services/ymodem/serial.service';
import { YmodemSendService, SendProgress } from '@services/ymodem/ymodem-send.service';
import { DeviceInfoService } from '@services/ymodem/device-info.service';
import { DeviceInfo, DeviceVerificationService } from '@services/ymodem/device-verification.service';
import { DeviceClockSyncOptions, DeviceClockSyncService } from '@services/ymodem/device-clock-sync.service';
import { VerifyFirmwareUpgradeResponse } from '@services/api-client/responses/verify-firmware-upgrade-response.type';
import { DeviceCalibrationStatus } from 'app/enums/device-calibration-status.enum';
import { UpgradeFile } from '@services/ymodem/file-source.interface';
import { Subscription, throttleTime, firstValueFrom, Observable, map } from 'rxjs';
import { Permission } from 'app/enums/permission.enum';
import { VersionComparisonStatus } from 'app/enums';
import { ApiClientService } from '@services/api-client/api-client.service';
import { CachedValuesService } from '@services/cached-values/cached-values.service';
import {
    CheckFirmwareVersionRequest,
    CheckFirmwareVersionResponse,
    AvailableFirmwareVersion
} from '@services/api-client';
import { RegionModel } from 'app/types/region.type';
import { DropdownItem } from '@shared/components/dropdown/dropdown.component';

@Component({
    selector: 'app-device-upgrade',
    standalone: false,
    templateUrl: './device-upgrade.component.html',
    styleUrls: ['./device-upgrade.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class DeviceUpgradeComponent implements OnInit, OnDestroy {
    public readonly clockSyncService = inject(DeviceClockSyncService);
    // Calibration is offered through the calibration section on this page, not through popups
    private readonly clockSyncOptions: DeviceClockSyncOptions = { showCalibrationPrompts: false };
    @ViewChild('step1Section')
    public step1Section?: ElementRef<HTMLElement>;

    @ViewChild('step2Section')
    public step2Section?: ElementRef<HTMLElement>;

    @ViewChild('step3Section')
    public step3Section?: ElementRef<HTMLElement>;

    @ViewChild('step4Section')
    public step4Section?: ElementRef<HTMLElement>;

    @ViewChild('selectedVersionSection')
    public selectedVersionSection?: ElementRef<HTMLElement>;

    @ViewChild('uploadProgressSection')
    public uploadProgressSection?: ElementRef<HTMLElement>;

    public currentGuideStep = 1;

    private state: ConnectState = {
        isConnected: false,
        isConnecting: false,
        isSupported: false,
        connectionError: null,
        currentDevice: null,
        receivedData: [],
        isTransferring: false,
        transferProgress: 0,
        transferSpeedBps: 0,
        currentFileName: '',
        filesReceived: 0,
        uploadStatus: {
            pending: 0,
            uploading: 0,
            completed: 0,
            failed: 0,
            totalFiles: 0
        },
        fileList: []
    };

    public permissions = Permission;
    private logicSubscription?: Subscription;
    private progressSubscription?: Subscription;

    public selectedFiles: UpgradeFile[] = [];
    public uploadProgress: SendProgress = {
        currentFileIndex: 0,
        totalFiles: 0,
        currentFileName: '',
        currentFileBytesTransferred: 0,
        currentFileSize: 0,
        overallBytesTransferred: 0,
        overallTotalBytes: 0,
        isSending: false,
        error: null
    };

    public deviceVersion: string | null = null;
    public isRetrievingVersion = false;
    public versionError: string | null = null;
    public softwareVersion: string | null = null;

    public deviceSerialNumber: string | null = null;
    public isRetrievingSerialNumber = false;
    public serialNumberError: string | null = null;

    // Calibration status of the connected device (from the silent verification)
    public calibrationDeviceInfo: DeviceInfo | null = null;
    public calibrationVerification: VerifyFirmwareUpgradeResponse | null = null;
    public readonly calibrationStatuses = DeviceCalibrationStatus;

    public availableVersions: AvailableFirmwareVersion[] = [];
    public versionComparisonStatus: VersionComparisonStatus | null = null;
    public readonly versionComparisonStatuses = VersionComparisonStatus;
    public lowerVersionsAvailable: AvailableFirmwareVersion[] = [];
    public isDowngradeMode = false;

    public get displayedVersions(): AvailableFirmwareVersion[] {
        return this.isDowngradeMode ? this.lowerVersionsAvailable : this.availableVersions;
    }

    public isCheckingVersions = false;
    public checkVersionsError: string | null = null;
    public loginRequired = false;
    public devicePassword: string | null = null;
    public selectedVersion: AvailableFirmwareVersion = null;
    public showEarlierVersions = false;
    public showDeviceVersionDetails = false;
    public isPreparingUpgrade = false;
    public upgradePhase: 'idle' | 'downloading' | 'uploading' = 'idle';

    // Verification state
    public isVerifying = false;
    public verificationStatus: 'idle' | 'verifying' | 'success' | 'failed' = 'idle';
    public verificationError: string | null = null;
    public verificationMessage: string | null = null;

    // Region selection state
    public regions$: Observable<DropdownItem<string>[]>;
    public selectedRegionId: string | null = null;
    public isLoadingRegions = false;
    public regionsError: string | null = null;
    private regionsMap: Map<string, string> = new Map();

    public constructor(
        private connectLogic: ConnectLogicService,
        private ymodemSendService: YmodemSendService,
        private serialService: SerialService,
        private deviceInfoService: DeviceInfoService,
        private deviceVerificationService: DeviceVerificationService,
        private apiClient: ApiClientService,
        private cachedValuesService: CachedValuesService,
        private cdr: ChangeDetectorRef
    ) {
        this.regions$ = this.cachedValuesService.regions$.pipe(
            map((data) => {
                // Build regions map for getRegionName() lookups
                this.regionsMap.clear();
                data.forEach((region) => {
                    this.regionsMap.set(region.id, region.name);
                });
                return data.map((item: RegionModel) => ({
                    label: item.name,
                    value: item.id
                }));
            })
        );
    }

    public ngOnInit(): void {
        // Reset all state when entering the component
        this.resetComponentState(true);

        this.logicSubscription = this.connectLogic.state$.pipe(throttleTime(200)).subscribe((state) => {
            const wasConnected = this.state.isConnected;
            this.state = state;
            if (wasConnected && !state.isConnected) {
                if (
                    this.currentGuideStep > 2 &&
                    !this.uploadProgress.isSending &&
                    this.uploadProgress.overallBytesTransferred === 0
                ) {
                    this.currentGuideStep = 2;
                }
            }
            this.cdr.markForCheck();
        });

        this.progressSubscription = this.ymodemSendService.progress$.pipe(throttleTime(200)).subscribe((progress) => {
            this.uploadProgress = progress;
            this.cdr.markForCheck();
        });
    }

    public ngOnDestroy(): void {
        this.logicSubscription?.unsubscribe();
        this.progressSubscription?.unsubscribe();
        // Clear all state when leaving the component
        this.resetComponentState(true);
        this.onDisconnect();
    }

    public onStep1Done(): void {
        if (this.currentGuideStep === 1) {
            this.currentGuideStep = 2;
            this.cdr.markForCheck();
            this.scrollToStep('step2Section', () => this.step2Section);
        }
    }

    public onStep3Done(): void {
        if (this.currentGuideStep === 3) {
            this.currentGuideStep = 4;
            this.cdr.markForCheck();
            this.scrollToStep('step4Section', () => this.step4Section);
        }
    }

    private scrollToStep(stepId: string, viewChildGetter?: () => ElementRef<HTMLElement> | undefined): void {
        setTimeout(() => {
            const element = viewChildGetter?.()?.nativeElement || document.getElementById(stepId);
            if (element) {
                element.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }
        }, 150);
    }

    private resetComponentState(resetGuideStep: boolean = false): void {
        if (resetGuideStep) {
            this.currentGuideStep = 1;
        }

        // Reset selected files and upload progress
        this.selectedFiles = [];
        this.uploadProgress = {
            currentFileIndex: 0,
            totalFiles: 0,
            currentFileName: '',
            currentFileBytesTransferred: 0,
            currentFileSize: 0,
            overallBytesTransferred: 0,
            overallTotalBytes: 0,
            isSending: false,
            error: null
        };
        this.ymodemSendService.resetProgress();

        // Reset device info
        this.deviceVersion = null;
        this.versionError = null;
        this.isRetrievingVersion = false;
        this.softwareVersion = null;
        this.deviceSerialNumber = null;
        this.serialNumberError = null;
        this.isRetrievingSerialNumber = false;
        this.calibrationDeviceInfo = null;
        this.calibrationVerification = null;

        // Reset version checking
        this.availableVersions = [];
        this.isCheckingVersions = false;
        this.checkVersionsError = null;
        this.loginRequired = false;
        this.devicePassword = null;
        this.selectedVersion = null;
        this.showEarlierVersions = false;
        this.showDeviceVersionDetails = false;

        // Reset upgrade state
        this.isPreparingUpgrade = false;
        this.upgradePhase = 'idle';

        // Reset verification state
        this.isVerifying = false;
        this.verificationStatus = 'idle';
        this.verificationError = null;
        this.verificationMessage = null;

        // Note: selectedRegionId is NOT reset here as it's selected before connection
        // and should persist across device reconnections

        this.cdr.markForCheck();
    }

    public onConnect(): void {
        // Reset all state for new device connection without resetting guide step
        this.resetComponentState(false);

        this.cdr.markForCheck();

        this.connectLogic
            .connect()
            .then(() => {
                // After connection, start buffered reading
                console.log('[YModem] Starting buffered read loop after connection');
                this.serialService.startBufferedReading();

                // Show "Retrieving..." message immediately to give user feedback
                this.isRetrievingVersion = true;
                this.versionError = null;

                this.cdr.markForCheck();

                // Wait for device's initial 'C' handshake, then retrieve version and serial number
                void this.waitForDeviceHandshakeThenRetrieveDeviceInfo();
            })
            .catch((error: unknown) => {
                const errorMsg: string = error instanceof Error ? error.message : JSON.stringify(error);
                console.error('Connection error:', errorMsg);
            });
    }

    private async waitForDeviceHandshakeThenRetrieveDeviceInfo(): Promise<void> {
        try {
            console.log('[YModem] Preparing device for communication...');

            // Clear any remaining data in buffer to ensure completely clean state
            console.log('[YModem] Draining buffer to clear all initial data...');
            this.serialService.drainBuffer();
            console.log('[YModem] Buffer cleared, ready to send commands');

            // Send a return character to ensure device is ready
            console.log('[YModem] Sending return character to ensure device is ready...');
            void this.serialService.sendData(new TextEncoder().encode('\r\n'));

            // Actively consume all buffered lines to ensure clean state
            console.log('[YModem] Consuming any remaining buffered data...');
            let consumedLines = 0;
            while (consumedLines < 10) {
                try {
                    const line = await this.serialService.waitForTextResponse(100);
                    console.log(`[YModem] Consumed buffered line ${consumedLines + 1}: "${line}"`);
                    consumedLines++;
                } catch {
                    // No more buffered data
                    break;
                }
            }

            // Wait a brief moment for device to process the return
            await new Promise((resolve) => setTimeout(resolve, 100));

            // Show "Retrieving..." message to give user feedback
            this.isRetrievingVersion = true;
            this.versionError = null;
            this.cdr.markForCheck();

            // Step 1: Retrieve device version first
            console.log('[YModem] Retrieving device version...');
            this.deviceVersion = await this.deviceInfoService.getDeviceVersion(this.serialService);
            console.log(`[YModem] ✓ Device version retrieved`);

            if (!this.deviceVersion || this.deviceVersion.trim().length === 0) {
                console.warn('[YModem] ✗ Device version is empty or invalid');
                this.versionError =
                    'Could not obtain device version. Please check that the device is not in send/receive mode and try again.';
                this.isRetrievingVersion = false;
                this.cdr.markForCheck();
                return;
            }

            this.softwareVersion = this.extractSoftwareVersion(this.deviceVersion);
            this.isRetrievingVersion = false;
            this.cdr.markForCheck();

            // Step 2: Check available firmware versions (which sets loginRequired and devicePassword)
            // This must complete before we can get serial number with proper login info
            this.checkAvailableFirmwareVersions();
        } catch (error: unknown) {
            const errorMsg: string = error instanceof Error ? error.message : JSON.stringify(error);
            console.warn(`[YModem] Device communication failed: ${errorMsg}`);
            this.versionError = errorMsg;
            this.isRetrievingVersion = false;
            this.cdr.markForCheck();
        }
    }

    private checkAvailableFirmwareVersions(): void {
        if (!this.deviceVersion) {
            this.checkVersionsError = 'Device version not available';
            console.error('[YModem] ✗ Device version not available');
            return;
        }

        this.isCheckingVersions = true;
        this.checkVersionsError = null;
        this.availableVersions = [];
        this.cdr.markForCheck();

        console.log('[YModem] Checking available firmware versions...');
        this.softwareVersion = this.extractSoftwareVersion(this.deviceVersion);
        const request: CheckFirmwareVersionRequest = {
            currentVersion: this.softwareVersion,
            regionId: this.selectedRegionId || undefined
        };

        this.apiClient.checkFirmwareVersion$(request).subscribe({
            next: (response) => {
                this.availableVersions = response.availableVersions;
                this.loginRequired = response.loginRequired;
                this.devicePassword = response.password;

                console.log(`[YModem] ✓ Found ${this.availableVersions.length} available versions`);
                if (this.loginRequired && this.devicePassword) {
                    console.log('[YModem] ℹ Device login required, password available');
                }
                this.isCheckingVersions = false;

                // Evaluate version comparison status before progressing
                this.evaluateVersionStatus(response);

                this.cdr.markForCheck();

                // Now that we know the login requirements, retrieve serial number and verify
                void this.retrieveSerialNumberAndVerify();
            },
            error: (error: unknown) => {
                const errorMsg: string = error instanceof Error ? error.message : JSON.stringify(error);
                this.checkVersionsError = errorMsg;
                console.error(`[YModem] ✗ Failed to check available versions: ${errorMsg}`);
                this.isCheckingVersions = false;
                this.cdr.markForCheck();
            }
        });
    }

    private evaluateVersionStatus(response: CheckFirmwareVersionResponse): void {
        this.versionComparisonStatus = response.comparisonStatus;
        this.lowerVersionsAvailable = response.lowerVersions ?? [];
        this.isDowngradeMode = false;

        switch (response.comparisonStatus) {
            case VersionComparisonStatus.NoPackages:
                console.log('[YModem] No firmware packages available for region');
                break;
            case VersionComparisonStatus.UpgradeAvailable:
                console.log(`[YModem] Upgrade available for device v${this.softwareVersion}`);
                if (this.currentGuideStep === 2) {
                    this.currentGuideStep = 3;
                    this.scrollToStep('step3Section', () => this.step3Section);
                }
                break;
            case VersionComparisonStatus.LatestInRegion:
                console.log(
                    `[YModem] Device has latest version in region (v${this.softwareVersion}), but a higher version exists in another region`
                );
                break;
            case VersionComparisonStatus.LatestGlobally:
                console.log(`[YModem] Device has latest version globally (v${this.softwareVersion})`);
                break;
        }
    }

    public onStartDowngrade(): void {
        this.isDowngradeMode = true;
        this.selectedVersion = this.lowerVersionsAvailable.length > 0 ? this.lowerVersionsAvailable[0] : null;
        this.currentGuideStep = 3;
        this.cdr.markForCheck();
        this.scrollToStep('step3Section', () => this.step3Section);
    }

    private async retrieveSerialNumberAndVerify(): Promise<void> {
        const connectionSession = this.serialService.connectionSessionId;
        try {
            console.log('[YModem] Retrieving device serial number for verification...');
            this.deviceSerialNumber = await this.deviceInfoService.getDeviceSerialNumber(
                this.serialService,
                this.loginRequired,
                this.devicePassword
            );
            console.log(`[YModem] ✓ Device serial number retrieved: ${this.deviceSerialNumber}`);
            this.cdr.markForCheck();

            // Perform silent verification now that we have all device info
            if (this.deviceVersion && this.softwareVersion && this.deviceSerialNumber) {
                const deviceDateTimeInfo = await this.deviceInfoService.getDeviceDateTime(
                    this.serialService,
                    this.softwareVersion
                );
                const deviceInfo: DeviceInfo = {
                    softwareVersion: this.softwareVersion,
                    versionText: this.deviceVersion,
                    serialNumber: this.deviceSerialNumber,
                    ...deviceDateTimeInfo
                };
                console.log('[YModem] Performing silent device verification...');
                const verification = await this.deviceVerificationService.verifyDeviceInfo(
                    deviceInfo,
                    this.serialService,
                    connectionSession,
                    this.clockSyncOptions
                );
                if (verification && this.isConnected && connectionSession === this.serialService.connectionSessionId) {
                    this.calibrationDeviceInfo = deviceInfo;
                    this.calibrationVerification = verification;
                    this.cdr.markForCheck();
                }
            }
        } catch (error: unknown) {
            const errorMsg: string = error instanceof Error ? error.message : JSON.stringify(error);
            console.error(`[YModem] ✗ Failed to retrieve serial number: ${errorMsg}`);
            // Don't block the UI - user can still proceed even if verification fails
        }
    }

    public async onCalibrate(): Promise<void> {
        const deviceInfo = this.calibrationDeviceInfo;
        const verification = this.calibrationVerification;
        if (!deviceInfo || !verification || !this.isConnected) return;

        const outcome = await this.clockSyncService.synchronize(this.serialService, deviceInfo, verification);
        if (outcome === 'calibrated' && this.calibrationVerification === verification) {
            this.calibrationVerification = {
                ...verification,
                deviceCalibrationStatusId: DeviceCalibrationStatus.BaselineRecorded
            };
        }
        this.cdr.markForCheck();
    }

    public onSelectRegion(regionId: string): void {
        this.selectedRegionId = regionId;
        console.log(`[YModem] Selected region ID: ${regionId}`);
        this.cdr.markForCheck();
    }

    public getRegionName(regionId: string): string {
        return this.regionsMap.get(regionId) || regionId;
    }

    private extractSoftwareVersion(versionString: string): string {
        // First try to find "Software Version: X.XXX"
        const match = versionString.match(/Software Version:\s*([^\s\n]+)/);
        if (match) {
            return match[1].trim();
        }

        // Fallback: Look for "Main Version:" section, then find the "Version:" line within it
        const mainSectionMatch = versionString.match(/Main Version:([\s\S]*?)(?=Core Version:|$)/);
        if (mainSectionMatch) {
            const mainSection = mainSectionMatch[1];
            // Match Version line with zero or more leading spaces (not indented)
            const versionMatch = mainSection.match(/^\s*Version:\s*([^\s\n]+)/m);
            if (versionMatch) {
                const mainVersion = versionMatch[1].trim();

                // Check for hardcoded version mappings based on Main and Core versions
                const coreVersionMatch = versionString.match(/Core Version:\s*\n?\s*([^\s\n]+)/);
                if (coreVersionMatch) {
                    const coreVersion = coreVersionMatch[1].trim();

                    // Hardcoded mapping for specific version combinations
                    if (mainVersion === '1.07' && coreVersion === '1.08') {
                        console.log(
                            '[YModem] Mapped Main Version 1.07 + Core Version 1.08 to Software Version 1.006 Pluto'
                        );
                        return '1.006';
                    } else if (mainVersion === '1.07' && coreVersion === '1.07') {
                        console.log(
                            '[YModem] Mapped Main Version 1.07 + Core Version 1.07 to Software Version 1.006 Mars'
                        );
                        return '1.005';
                    }
                }

                console.log('[YModem] Using Main Version value as software version fallback');
                return 'unknown';
            }
        }

        // Last resort: return the entire string
        console.warn('[YModem] Could not extract software version from device response');
        return versionString;
    }

    public onSelectVersion(version: AvailableFirmwareVersion): void {
        console.log(`[YModem] Version selected: ${version.name} (${version.version})`);
        this.selectedVersion = version;
        this.cdr.markForCheck();

        // Scroll to the selected version details section
        this.scrollToStep('selectedVersionSection', () => this.selectedVersionSection);
    }

    public async onStartUpgrade(): Promise<void> {
        if (this.clockSyncService.isSynchronizing) return;
        if (!this.selectedVersion) {
            console.warn('[YModem] No version selected for upgrade');
            return;
        }

        this.isPreparingUpgrade = true;
        this.upgradePhase = 'downloading';
        this.verificationStatus = 'idle';
        this.verificationError = null;
        this.verificationMessage = null;
        this.uploadProgress = {
            currentFileIndex: 0,
            totalFiles: 0,
            currentFileName: 'Preparing download...',
            currentFileBytesTransferred: 0,
            currentFileSize: 0,
            overallBytesTransferred: 0,
            overallTotalBytes: 0,
            isSending: false,
            error: null
        };
        this.cdr.markForCheck();

        try {
            console.log(`[YModem] Starting upgrade for version: ${this.selectedVersion.name}`);

            // Step 1: Get metadata for all files in this version
            console.log('[YModem] Fetching firmware file metadata from server...');
            const fileMetadata = await firstValueFrom(this.apiClient.getFirmwareFiles$(this.selectedVersion.versionId));

            if (!fileMetadata?.files || fileMetadata.files.length === 0) {
                throw new Error('No firmware files found for this version');
            }

            console.log(`[YModem] Found ${fileMetadata.files.length} firmware files, downloading in parallel...`);

            // Initialize progress tracking
            this.uploadProgress.totalFiles = fileMetadata.files.length;
            this.uploadProgress.currentFileIndex = 0;
            this.uploadProgress.currentFileName = 'Downloading file 1/' + fileMetadata.files.length + '...';
            this.uploadProgress.isSending = true;
            this.uploadProgress.overallBytesTransferred = 0;
            this.uploadProgress.overallTotalBytes = 0;
            this.cdr.markForCheck();

            // Scroll to the upload progress section when download starts
            this.scrollToStep('uploadProgressSection', () => this.uploadProgressSection);

            // Track completed downloads
            const downloadsMutex: { completed: number } = { completed: 0 };

            // Step 2: Download all files in parallel with progress tracking
            const downloadPromises: Promise<Blob>[] = [];
            for (let i = 0; i < fileMetadata.files.length; i++) {
                const file = fileMetadata.files[i];
                downloadPromises.push(
                    this.downloadFileWithProgress(file.id, i, fileMetadata.files.length, downloadsMutex)
                );
            }

            const fileBlobs = await Promise.all(downloadPromises);
            console.log(`[YModem] ✓ Downloaded all ${fileBlobs.length} firmware files`);

            // Step 3: Convert blobs to upgrade files for YModem transfer
            const upgradedFiles: UpgradeFile[] = [];
            for (let i = 0; i < fileBlobs.length; i++) {
                const blob = fileBlobs[i];
                upgradedFiles.push({
                    name: fileMetadata.files[i].name,
                    data: new Uint8Array(await blob.arrayBuffer()),
                    size: blob.size
                });
            }
            this.selectedFiles = upgradedFiles;

            console.log('[YModem] Files prepared, starting YModem upload to device...');
            this.upgradePhase = 'uploading';
            this.uploadProgress.isSending = true;
            this.uploadProgress.currentFileIndex = 0;
            this.cdr.markForCheck();

            await this.onUpload();
        } catch (error: unknown) {
            const errorMsg: string = error instanceof Error ? error.message : JSON.stringify(error);
            this.uploadProgress.error = errorMsg;
            this.uploadProgress.isSending = false;
            console.error(`[YModem] Failed to start upgrade: ${errorMsg}`);
            this.upgradePhase = 'idle';
            this.cdr.markForCheck();

            // Scroll to the error message
            setTimeout(() => {
                const errorElements = document.querySelectorAll('.error-message');
                if (errorElements.length > 0) {
                    (errorElements[errorElements.length - 1] as HTMLElement).scrollIntoView({
                        behavior: 'smooth',
                        block: 'center'
                    });
                }
            }, 100);
        } finally {
            this.isPreparingUpgrade = false;
            this.cdr.markForCheck();
        }
    }

    private async downloadFileWithProgress(
        fileId: string,
        fileIndex: number,
        totalFiles: number,
        downloadsMutex: { completed: number }
    ): Promise<Blob> {
        try {
            const blob = await firstValueFrom(this.apiClient.downloadFirmwareFile$(fileId));

            // Update progress
            if (blob instanceof Blob) {
                // Increment completed counter
                downloadsMutex.completed++;
                const completed = downloadsMutex.completed;

                this.uploadProgress.currentFileIndex = completed;
                this.uploadProgress.currentFileName = `Downloading file ${completed}/${totalFiles}...`;
                this.uploadProgress.currentFileBytesTransferred = blob.size;
                this.uploadProgress.currentFileSize = blob.size;
                this.uploadProgress.overallBytesTransferred += blob.size;
                this.uploadProgress.overallTotalBytes += blob.size;
                this.cdr.markForCheck();
            }

            return blob;
        } catch (error: unknown) {
            const errorMsg: string = error instanceof Error ? error.message : JSON.stringify(error);
            console.error('Download error:', errorMsg);
            throw new Error(`Failed to download file ${fileIndex + 1}`);
        }
    }

    public onDisconnect(): void {
        this.deviceVersion = null;
        this.versionError = null;
        this.isRetrievingVersion = false;
        this.deviceSerialNumber = null;
        this.serialNumberError = null;
        this.isRetrievingSerialNumber = false;
        this.calibrationDeviceInfo = null;
        this.calibrationVerification = null;
        this.availableVersions = [];
        this.versionComparisonStatus = null;
        this.lowerVersionsAvailable = [];
        this.isDowngradeMode = false;
        this.isCheckingVersions = false;
        this.checkVersionsError = null;
        this.loginRequired = false;
        this.devicePassword = null;
        this.selectedVersion = null;
        this.isPreparingUpgrade = false;
        this.upgradePhase = 'idle';
        if (
            this.currentGuideStep > 2 &&
            !this.uploadProgress.isSending &&
            this.uploadProgress.overallBytesTransferred === 0
        ) {
            this.currentGuideStep = 2;
        }
        this.connectLogic.disconnect().catch((error: unknown) => {
            const errorMsg: string = error instanceof Error ? error.message : JSON.stringify(error);
            console.error('Disconnect error:', errorMsg);
        });
    }

    // Getters to expose state properties to template
    public get isConnected(): boolean {
        return this.state.isConnected;
    }

    public get isConnecting(): boolean {
        return this.state.isConnecting;
    }

    public get isSupported(): boolean {
        return this.state.isSupported;
    }

    public get connectionError(): string | null {
        return this.state.connectionError;
    }

    public get currentDevice(): SerialDeviceInfo | null {
        return this.state.currentDevice;
    }

    public get deviceDisplayName(): string {
        if (!this.state.currentDevice) {
            return '';
        }
        const device = this.state.currentDevice;
        return device.portName || 'Unknown Device';
    }

    public async onUpload(): Promise<void> {
        if (this.clockSyncService.isSynchronizing) return;
        if (!this.isConnected) {
            return;
        }

        try {
            // Send 'C' to activate device receive mode for YModem transfer
            console.log('[YModem] Sending C to activate device receive mode...');
            void this.serialService.sendData(new Uint8Array([SZ_CODES.C])).catch((error: unknown) => {
                const errorMsg: string = error instanceof Error ? error.message : JSON.stringify(error);
                console.error('Error sending initial C:', errorMsg);
            });

            // Give device a moment to switch to receive mode
            await new Promise((resolve) => setTimeout(resolve, 50));

            await this.ymodemSendService.sendBatch(this.selectedFiles);
            console.log('Upload completed successfully');
            this.uploadProgress.isSending = false;
            this.cdr.markForCheck();

            // Notify server of upload completion
            if (this.selectedVersion && this.deviceSerialNumber) {
                try {
                    console.log('[YModem] Notifying server of upload completion...');
                    await firstValueFrom(
                        this.apiClient.notifyFirmwareUpload$({
                            versionId: this.selectedVersion.versionId,
                            serialNumber: this.deviceSerialNumber
                        })
                    );
                    console.log('[YModem] ✓ Server notification sent successfully');
                } catch (notifyError: unknown) {
                    const notifyMsg: string =
                        notifyError instanceof Error ? notifyError.message : JSON.stringify(notifyError);
                    console.warn(`[YModem] Failed to notify server: ${notifyMsg}`);
                    // Don't fail the upload if notification fails
                }
            }

            // Scroll to verification widget
            setTimeout(() => {
                const verificationWidget = document.querySelector('.verification-widget');
                if (verificationWidget) {
                    (verificationWidget as HTMLElement).scrollIntoView({ behavior: 'smooth', block: 'center' });
                }
            }, 200);
        } catch (error: unknown) {
            // Log only the error message without stack trace for cleaner console output
            let errorMessage = 'Unknown error';
            if (error instanceof Error) {
                errorMessage = error.message;
            } else if (typeof error === 'string') {
                errorMessage = error;
            }
            console.error(`[YModem] Upload failed: ${errorMessage}`);
            this.uploadProgress.error = errorMessage;
            this.uploadProgress.isSending = false;
            this.upgradePhase = 'idle';
            this.cdr.markForCheck();

            // Scroll to the error message
            setTimeout(() => {
                const errorElements = document.querySelectorAll('.error-message');
                if (errorElements.length > 0) {
                    (errorElements[errorElements.length - 1] as HTMLElement).scrollIntoView({
                        behavior: 'smooth',
                        block: 'center'
                    });
                }
            }, 100);
        }
    }

    public formatBytes(bytes: number): string {
        if (bytes === 0) return '0 B';
        const k = 1024;
        const sizes = ['B', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    }

    public getProgressPercentage(): number {
        if (this.uploadProgress.overallTotalBytes === 0) return 0;
        return Math.round((this.uploadProgress.overallBytesTransferred / this.uploadProgress.overallTotalBytes) * 100);
    }

    public isUploadComplete(): boolean {
        return (
            !this.uploadProgress.isSending &&
            this.uploadProgress.overallBytesTransferred > 0 &&
            this.uploadProgress.overallBytesTransferred === this.uploadProgress.overallTotalBytes
        );
    }

    public async onVerifyUpgrade(): Promise<void> {
        if (this.clockSyncService.isSynchronizing) return;
        this.isVerifying = true;
        this.verificationStatus = 'verifying';
        this.verificationError = null;
        this.verificationMessage = 'Connecting to device for verification...';
        this.cdr.markForCheck();

        // Scroll to verification widget
        setTimeout(() => {
            const verificationWidget = document.querySelector('.verification-widget');
            if (verificationWidget) {
                (verificationWidget as HTMLElement).scrollIntoView({ behavior: 'smooth', block: 'center' });
            }
        }, 100);

        try {
            // Step 1: Connect to device
            console.log('[YModem] Starting verification: connecting to device...');
            await this.connectLogic.connect();
            this.serialService.startBufferedReading();

            // Clear buffer and wait for stability
            console.log('[YModem] Clearing buffer and waiting for device stability...');
            this.serialService.drainBuffer();
            await new Promise((resolve) => setTimeout(resolve, 200));

            await this.serialService.sendData(new TextEncoder().encode('\r\n'));
            for (let consumedLines = 0; consumedLines < 10; consumedLines++) {
                try {
                    await this.serialService.waitForTextResponse(100);
                } catch {
                    break;
                }
            }
            await new Promise((resolve) => setTimeout(resolve, 100));

            // Steps 2-4: Use verification service to retrieve device info and verify
            this.verificationMessage = 'Retrieving device information and verifying...';
            this.cdr.markForCheck();

            const verificationResult = await this.deviceVerificationService.verifyDevice(
                this.serialService,
                this.loginRequired,
                this.devicePassword,
                false,
                this.clockSyncOptions
            );

            // Step 5: Display result
            if (verificationResult.success && verificationResult.verificationResponse?.isVerified) {
                this.verificationStatus = 'success';
                this.verificationMessage =
                    '✓ The device firmware has now been successfully upgraded and verified. You may now disconnect the TetraGraph and continue operating the device with the latest software features.';
                console.log('[YModem] ✓ Firmware verification successful');
            } else if (verificationResult.verificationResponse) {
                this.verificationStatus = 'failed';
                this.verificationMessage = `✗ Verification failed: ${verificationResult.verificationResponse.message}`;
                console.warn(
                    '[YModem] ✗ Firmware verification failed:',
                    verificationResult.verificationResponse.message
                );
            } else if (verificationResult.deviceInfo) {
                this.verificationStatus = 'failed';
                this.verificationError = 'Failed to verify device with server';
                this.verificationMessage = null;
                console.error('[YModem] ✗ Device verification failed');
            } else {
                this.verificationStatus = 'failed';
                this.verificationError = 'Failed to retrieve device information';
                this.verificationMessage = null;
                console.error('[YModem] ✗ Failed to retrieve device info');
            }
            this.cdr.markForCheck();

            // Scroll to the verification result
            setTimeout(() => {
                const verificationWidget = document.querySelector('.verification-widget');
                if (verificationWidget) {
                    (verificationWidget as HTMLElement).scrollIntoView({ behavior: 'smooth', block: 'center' });
                }
            }, 150);
        } catch (error: unknown) {
            const errorMsg: string = error instanceof Error ? error.message : JSON.stringify(error);
            this.verificationStatus = 'failed';
            this.verificationError = errorMsg;
            this.verificationMessage = null;
            console.error(`[YModem] Verification error: ${errorMsg}`);
            this.cdr.markForCheck();

            // Scroll to the error message
            setTimeout(() => {
                const errorElements = document.querySelectorAll('.verification-widget .error-message');
                if (errorElements.length > 0) {
                    (errorElements[errorElements.length - 1] as HTMLElement).scrollIntoView({
                        behavior: 'smooth',
                        block: 'center'
                    });
                }
            }, 100);
        } finally {
            this.isVerifying = false;
            this.cdr.markForCheck();
            this.onDisconnect();
        }
    }
}
